'use client';

import { cn } from '@commander/utils';
import {
  DndContext,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  type DropAnimation,
  defaultDropAnimation,
  defaultDropAnimationSideEffects,
  KeyboardSensor,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

interface KanbanContextProps<T> {
  columns: Record<string, T[]>;
  setColumns: (columns: Record<string, T[]>) => void;
  getItemId: (item: T) => string;
  columnIds: string[];
  activeId: UniqueIdentifier | null;
  setActiveId: (id: UniqueIdentifier | null) => void;
  findContainer: (id: UniqueIdentifier) => string | undefined;
  isColumn: (id: UniqueIdentifier) => boolean;
  overColumnId: UniqueIdentifier | null;
  setOverColumnId: (id: UniqueIdentifier | null) => void;
}

// Default-context noops — overridden by the real Kanban provider at runtime.
const noop = () => undefined;

const KanbanContext = React.createContext<KanbanContextProps<any>>({
  columns: {},
  setColumns: noop,
  getItemId: () => '',
  columnIds: [],
  activeId: null,
  setActiveId: noop,
  findContainer: () => undefined,
  isColumn: () => false,
  overColumnId: null,
  setOverColumnId: noop,
});

const ColumnContext = React.createContext<{
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners | undefined;
  isDragging?: boolean;
  disabled?: boolean;
}>({
  attributes: {} as DraggableAttributes,
  listeners: undefined,
  isDragging: false,
  disabled: false,
});

const ItemContext = React.createContext<{
  listeners: DraggableSyntheticListeners | undefined;
  isDragging?: boolean;
  disabled?: boolean;
}>({
  listeners: undefined,
  isDragging: false,
  disabled: false,
});

const dropAnimationConfig: DropAnimation = {
  ...defaultDropAnimation,
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.4',
      },
    },
  }),
};

export interface KanbanMoveEvent {
  event: DragEndEvent;
  activeContainer: string;
  activeIndex: number;
  overContainer: string;
  overIndex: number;
}

export interface KanbanRootProps<T> {
  value: Record<string, T[]>;
  onValueChange: (value: Record<string, T[]>) => void;
  getItemValue: (item: T) => string;
  children: React.ReactNode;
  className?: string;
  onMove?: (event: KanbanMoveEvent) => void;
  // Fires whenever the active drag id changes — passes the active item/column id
  // when a drag starts, and `null` on drag end/cancel. Lets consumers compute
  // per-column UI state during drag (e.g. greying out invalid drop targets).
  onActiveChange?: (activeId: UniqueIdentifier | null) => void;
}

function Kanban<T>({
  value,
  onValueChange,
  getItemValue,
  children,
  className,
  onMove,
  onActiveChange,
}: KanbanRootProps<T>) {
  const columns = value;
  const setColumns = onValueChange;
  const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null);

  // Mirror activeId changes out to consumers so they can react to drag start/end
  // (e.g. compute disabled drop targets). Effect rather than wrapping the
  // setter so we don't have to thread an unstable callback through useCallbacks.
  const onActiveChangeRef = React.useRef(onActiveChange);
  React.useEffect(() => {
    onActiveChangeRef.current = onActiveChange;
  }, [onActiveChange]);
  React.useEffect(() => {
    onActiveChangeRef.current?.(activeId);
  }, [activeId]);
  const [overColumnId, setOverColumnId] = React.useState<UniqueIdentifier | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const columnIds = React.useMemo(() => Object.keys(columns), [columns]);

  const isColumn = React.useCallback(
    (id: UniqueIdentifier) => columnIds.includes(id as string),
    [columnIds]
  );

  const findContainer = React.useCallback(
    (id: UniqueIdentifier) => {
      const idStr = id as string;

      // Check if it's a column
      if (isColumn(id)) return id as string;

      // Check if it's a drop zone and extract the column value
      if (idStr.includes('drop-zone')) {
        // Extract column ID from "drop-zone-{columnId}-{position}"
        // Strip the "drop-zone-" prefix
        const withoutPrefix = idStr.replace('drop-zone-', '');

        // Find the last dash to separate columnId from position
        const lastDashIndex = withoutPrefix.lastIndexOf('-');
        if (lastDashIndex > 0) {
          const columnId = withoutPrefix.substring(0, lastDashIndex);
          if (columnIds.includes(columnId)) {
            return columnId;
          }
        }
      }

      // Check if it's an item in a column
      return columnIds.find((key) => columns[key].some((item) => getItemValue(item) === id));
    },
    [columns, columnIds, getItemValue, isColumn]
  );

  const handleDragStart = React.useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragOver = React.useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;

      // Update over column for visual feedback
      if (over) {
        // If hovering directly over a column (empty or not)
        if (isColumn(over.id) && !isColumn(active.id)) {
          setOverColumnId(over.id);
        } else {
          // If hovering over an item, get its container
          const overContainer = findContainer(over.id);
          if (overContainer && !isColumn(active.id)) {
            setOverColumnId(overContainer);
          }
        }
      } else {
        setOverColumnId(null);
      }

      if (onMove) {
        return;
      }

      if (!over) return;

      if (isColumn(active.id)) return;

      const activeContainer = findContainer(active.id);
      const overContainer = findContainer(over.id);

      // Only handle moving items between different columns or same column reordering
      if (!activeContainer || !overContainer) {
        return;
      }

      const activeItems = columns[activeContainer];
      const overItems = columns[overContainer];

      const activeIndex = activeItems.findIndex((item: T) => getItemValue(item) === active.id);
      let overIndex = overItems.findIndex((item: T) => getItemValue(item) === over.id);

      // Determine the drop position based on the droppable element
      const overId = over.id as string;

      // Check if we're dropping on a custom droppable zone between items
      if (overId.includes('drop-zone')) {
        // Extract the position from the drop-zone id
        const parts = overId.split('-');
        const position = parseInt(parts[parts.length - 1], 10);
        overIndex = position;
      } else if (isColumn(over.id) || !over) {
        // If dropping on the column itself, position at the end
        overIndex = overItems.length;
      }

      // Don't update if we're in the same container and position hasn't changed
      if (activeContainer === overContainer && activeIndex === overIndex) {
        return;
      }

      // Cross-column drag-over deliberately does NOT commit the move. Doing so
      // fires onValueChange on every hover — downstream consumers (e.g. CRM
      // moveDealToStage) would issue an API call per hover, letting a user
      // "snake" a deal through every stage by dragging without releasing.
      // Cross-column moves are committed once on drop in handleDragEnd.
      if (activeContainer === overContainer && activeIndex !== overIndex) {
        // Same-column reorder still updates live so items shift to make space.
        // No deal-stage change here, so no API-call snake risk.
        const newOverItems = [...overItems];
        const [movedItem] = newOverItems.splice(activeIndex, 1);
        newOverItems.splice(overIndex, 0, movedItem);

        setColumns({
          ...columns,
          [overContainer]: newOverItems,
        });
      }
    },
    [findContainer, getItemValue, isColumn, setColumns, columns, onMove]
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      setOverColumnId(null);

      // Don't allow dropping outside of valid drop zones
      if (!over) {
        console.log('Drag cancelled - no valid drop target');
        return;
      }

      // Validate that we're dropping in a valid kanban column
      const targetContainer = findContainer(over.id);
      const targetColumn = isColumn(over.id) ? over.id : targetContainer;

      // If we can't find a valid column container, cancel the drop
      if (!targetColumn || !columnIds.includes(targetColumn as string)) {
        console.log('Drag cancelled - invalid drop target');
        return;
      }

      // Don't allow dropping columns outside the board
      if (isColumn(active.id) && !isColumn(over.id)) {
        console.log('Drag cancelled - column drop only allowed on other columns');
        return;
      }

      // Handle item move callback
      if (onMove && !isColumn(active.id)) {
        const activeContainer = findContainer(active.id);
        const overContainer = findContainer(over.id);

        if (activeContainer && overContainer) {
          const activeIndex = columns[activeContainer].findIndex(
            (item: T) => getItemValue(item) === active.id
          );
          const overIndex = isColumn(over.id)
            ? columns[overContainer].length
            : columns[overContainer].findIndex((item: T) => getItemValue(item) === over.id);

          onMove({
            event,
            activeContainer,
            activeIndex,
            overContainer,
            overIndex,
          });
        }
        return;
      }

      // Handle column reordering
      if (isColumn(active.id) && isColumn(over.id)) {
        const activeIndex = columnIds.indexOf(active.id as string);
        const overIndex = columnIds.indexOf(over.id as string);
        if (activeIndex !== overIndex) {
          const newOrder = arrayMove(Object.keys(columns), activeIndex, overIndex);
          const newColumns: Record<string, T[]> = {};
          newOrder.forEach((key) => {
            newColumns[key] = columns[key];
          });
          setColumns(newColumns);
        }
        return;
      }

      const activeContainer = findContainer(active.id);
      const overContainer = findContainer(over.id);

      if (activeContainer && overContainer) {
        if (activeContainer === overContainer) {
          // Item reordering within the same column. Mirrors the cross-column
          // branch so drop-zone-* targets resolve to a real index instead of
          // findIndex's -1.
          const container = activeContainer;
          const items = columns[container];
          const activeIndex = items.findIndex((item: T) => getItemValue(item) === active.id);

          let overIndex: number;
          const overId = over.id as string;
          if (overId.includes('drop-zone')) {
            const parts = overId.split('-');
            overIndex = parseInt(parts[parts.length - 1], 10);
          } else if (isColumn(over.id)) {
            overIndex = items.length - 1;
          } else {
            overIndex = items.findIndex((item: T) => getItemValue(item) === over.id);
          }

          const lastIndex = items.length - 1;
          if (Number.isFinite(overIndex) && overIndex >= 0 && lastIndex >= 0) {
            const clampedOverIndex = Math.min(Math.max(overIndex, 0), lastIndex);
            if (activeIndex >= 0 && activeIndex !== clampedOverIndex) {
              setColumns({
                ...columns,
                [container]: arrayMove(items, activeIndex, clampedOverIndex),
              });
            }
          }
        } else {
          // Cross-column drop. Drag-over no longer commits this move (see the
          // matching comment in handleDragOver), so we commit it here so the
          // consumer's onValueChange fires exactly once with the final state.
          const activeItems = columns[activeContainer];
          const overItems = columns[overContainer];
          const activeIndex = activeItems.findIndex((item: T) => getItemValue(item) === active.id);

          let overIndex: number;
          const overId = over.id as string;
          if (overId.includes('drop-zone')) {
            const parts = overId.split('-');
            overIndex = parseInt(parts[parts.length - 1], 10);
          } else if (isColumn(over.id)) {
            overIndex = overItems.length;
          } else {
            const idx = overItems.findIndex((item: T) => getItemValue(item) === over.id);
            overIndex = idx >= 0 ? idx : overItems.length;
          }

          if (activeIndex >= 0) {
            const newActiveItems = [...activeItems];
            const [movedItem] = newActiveItems.splice(activeIndex, 1);
            const newOverItems = [...overItems];
            newOverItems.splice(overIndex, 0, movedItem);

            setColumns({
              ...columns,
              [activeContainer]: newActiveItems,
              [overContainer]: newOverItems,
            });
          }
        }
      }
    },
    [columnIds, columns, findContainer, getItemValue, isColumn, setColumns, onMove]
  );

  const contextValue = React.useMemo(
    () => ({
      columns,
      setColumns,
      getItemId: getItemValue,
      columnIds,
      activeId,
      setActiveId,
      findContainer,
      isColumn,
      overColumnId,
      setOverColumnId,
    }),
    [columns, setColumns, getItemValue, columnIds, activeId, findContainer, isColumn, overColumnId]
  );

  return (
    <KanbanContext.Provider value={contextValue}>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div
          data-slot="kanban"
          data-dragging={activeId !== null}
          className={cn(className, activeId && 'select-none')}
        >
          {children}
        </div>
      </DndContext>
    </KanbanContext.Provider>
  );
}

export interface KanbanBoardProps {
  className?: string;
  children: React.ReactNode;
}

function KanbanBoard({ children, className }: KanbanBoardProps) {
  const { columnIds } = React.useContext(KanbanContext);

  return (
    <SortableContext items={columnIds} strategy={rectSortingStrategy}>
      <div data-slot="kanban-board" className={cn('flex gap-2 overflow-x-auto pb-4', className)}>
        {children}
      </div>
    </SortableContext>
  );
}

export interface KanbanColumnProps {
  value: string;
  className?: string;
  children: React.ReactNode;
  disabled?: boolean;
  emptyDropMessage?: string;
}

function KanbanColumn({
  value,
  className,
  children,
  disabled,
  emptyDropMessage,
}: KanbanColumnProps) {
  const {
    setNodeRef,
    transform,
    transition,
    attributes,
    listeners,
    isDragging: isSortableDragging,
  } = useSortable({
    id: value,
    disabled,
  });

  const { activeId, isColumn, overColumnId, columns, getItemId } = React.useContext(KanbanContext);
  const isColumnDragging = activeId ? isColumn(activeId) : false;
  const isDraggingOver = activeId && !isColumn(activeId) && overColumnId === value;
  const isEmpty = columns[value]?.length === 0;
  const hasActiveItem = activeId && !columns[value]?.some((item) => getItemId(item) === activeId);

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  } as React.CSSProperties;

  return (
    <ColumnContext.Provider
      value={{ attributes, listeners, isDragging: isColumnDragging, disabled }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: column wrapper hosts a sortable container; semantic <button> would break dnd-kit's drop target. */}
      <div
        data-slot="kanban-column"
        data-value={value}
        data-dragging={isSortableDragging}
        data-drag-over={isDraggingOver}
        data-empty={isEmpty}
        data-disabled={disabled}
        ref={setNodeRef}
        style={style}
        className={cn(
          'group/kanban-column flex flex-col flex-shrink-0 transition-all duration-200 relative',
          isSortableDragging && 'opacity-50',
          disabled && 'opacity-50',
          isDraggingOver && [
            'ring-2 ring-primary/30 ring-offset-2 ring-offset-background rounded-lg',
            'shadow-lg scale-[1.02]',
          ],
          className
        )}
        role="button"
        tabIndex={isDraggingOver ? 0 : undefined}
        aria-label={isDraggingOver ? `${emptyDropMessage || 'Drop items here'}` : undefined}
      >
        {children}

        {/* Full-column drop zone overlay for empty columns */}
        {isEmpty && isDraggingOver && hasActiveItem && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-primary/5 dark:bg-primary/10 border-2 border-dashed border-primary/40 rounded-lg m-1 pointer-events-none select-none animate-in fade-in-0 zoom-in-95 duration-200">
            <div className="flex flex-col items-center gap-2 text-center p-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-primary/60"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  role="img"
                  aria-label="Drop indicator"
                >
                  <title>Drop indicator</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <div className="text-sm font-medium text-primary/80 dark:text-primary/70">
                {emptyDropMessage || 'Drop items here'}
              </div>
              <div className="text-xs text-muted-foreground">Release to drop</div>
            </div>
          </div>
        )}
      </div>
    </ColumnContext.Provider>
  );
}

export interface KanbanColumnHandleProps {
  asChild?: boolean;
  className?: string;
  children?: React.ReactNode;
  cursor?: boolean;
}

function KanbanColumnHandle({
  asChild,
  className,
  children,
  cursor = true,
}: KanbanColumnHandleProps) {
  const { attributes, listeners, isDragging, disabled } = React.useContext(ColumnContext);

  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="kanban-column-handle"
      data-dragging={isDragging}
      data-disabled={disabled}
      {...attributes}
      {...listeners}
      className={cn(
        'transition-opacity group-hover/kanban-column:opacity-100',
        cursor && (isDragging ? '!cursor-grabbing' : '!cursor-grab'),
        className
      )}
    >
      {children}
    </Comp>
  );
}

export interface KanbanItemProps {
  value: string;
  asChild?: boolean;
  className?: string;
  children: React.ReactNode;
  disabled?: boolean;
}

function KanbanItem({ value, asChild = false, className, children, disabled }: KanbanItemProps) {
  const {
    setNodeRef,
    transform,
    transition,
    attributes,
    listeners,
    isDragging: isSortableDragging,
  } = useSortable({
    id: value,
    disabled,
  });

  const { activeId, isColumn } = React.useContext(KanbanContext);
  const isItemDragging = activeId ? !isColumn(activeId) : false;

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  } as React.CSSProperties;

  // dnd-kit's KeyboardSensor starts a drag on Space/Enter and calls preventDefault.
  // Keydowns from dialogs rendered inside an item bubble here through the React
  // portal, so without this guard nobody can type a space or newline in them.
  // Only keys pressed on the item itself may start a keyboard drag.
  const itemListeners = listeners && {
    ...listeners,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.target !== event.currentTarget) return;
      listeners.onKeyDown?.(event);
    },
  };

  const Comp = asChild ? Slot : 'div';

  return (
    <ItemContext.Provider value={{ listeners, isDragging: isItemDragging, disabled }}>
      <Comp
        data-slot="kanban-item"
        data-value={value}
        data-dragging={isSortableDragging}
        data-disabled={disabled}
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...itemListeners}
        className={cn(isSortableDragging && 'opacity-50', disabled && 'opacity-50', className)}
      >
        {children}
      </Comp>
    </ItemContext.Provider>
  );
}

export interface KanbanItemHandleProps {
  asChild?: boolean;
  className?: string;
  children?: React.ReactNode;
  cursor?: boolean;
}

function KanbanItemHandle({ asChild, className, children, cursor = true }: KanbanItemHandleProps) {
  const { listeners, isDragging, disabled } = React.useContext(ItemContext);

  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="kanban-item-handle"
      data-dragging={isDragging}
      data-disabled={disabled}
      {...listeners}
      className={cn(cursor && (isDragging ? '!cursor-grabbing' : '!cursor-grab'), className)}
    >
      {children}
    </Comp>
  );
}

export interface KanbanColumnContentProps {
  value: string;
  className?: string;
  children: React.ReactNode;
  /** Forwarded to the scroll container — lets consumers wire infinite scroll. */
  onScroll?: React.UIEventHandler<HTMLDivElement>;
}

function KanbanColumnContent({ value, className, children, onScroll }: KanbanColumnContentProps) {
  const { columns, getItemId, activeId, overColumnId } = React.useContext(KanbanContext);

  const itemIds = React.useMemo(() => columns[value].map(getItemId), [columns, getItemId, value]);
  const isEmpty = columns[value].length === 0;
  const isDraggingOver = activeId && overColumnId === value;

  return (
    <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
      <div
        data-slot="kanban-column-content"
        data-value={value}
        data-empty={isEmpty}
        data-drag-over={isDraggingOver}
        onScroll={onScroll}
        className={cn(
          'flex flex-col gap-2 transition-all duration-200 ease-in-out',
          isEmpty && activeId && 'min-h-[200px]', // Minimum height for empty columns when dragging
          isDraggingOver && 'py-1', // Add padding when dragging over to create more drop space
          className
        )}
      >
        {children}
        {/* Add invisible drop indicators at the beginning and end when dragging over */}
        {isDraggingOver && !isEmpty && (
          <>
            <div className="h-1 w-full border-2 border-dashed border-primary/30 rounded-full opacity-0" />
            <div className="h-1 w-full border-2 border-dashed border-primary/30 rounded-full opacity-0" />
          </>
        )}
      </div>
    </SortableContext>
  );
}

export interface KanbanDropZoneProps {
  value: string;
  position: number;
  className?: string;
}

function KanbanDropZone({ value, position, className }: KanbanDropZoneProps) {
  const { activeId } = React.useContext(KanbanContext);

  if (!activeId) return null;

  return (
    <div
      data-slot="kanban-drop-zone"
      data-value={`drop-zone-${value}-${position}`}
      className={cn(
        'h-3 w-full rounded transition-all duration-200 select-none pointer-events-none',
        'border border-dashed border-gray-300 dark:border-gray-600',
        'opacity-40',
        className
      )}
    />
  );
}

export interface KanbanOverlayProps {
  className?: string;
  children?:
    | React.ReactNode
    | ((params: { value: UniqueIdentifier; variant: 'column' | 'item' }) => React.ReactNode);
}

function KanbanOverlay({ children, className }: KanbanOverlayProps) {
  const { activeId, isColumn } = React.useContext(KanbanContext);
  const [dimensions, setDimensions] = React.useState<{ width: number; height: number } | null>(
    null
  );

  React.useEffect(() => {
    if (activeId) {
      const element = document.querySelector(
        `[data-slot="kanban-${isColumn(activeId) ? 'column' : 'item'}"][data-value="${activeId}"]`
      );
      if (element) {
        const rect = element.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    } else {
      setDimensions(null);
    }
  }, [activeId, isColumn]);

  const style = {
    width: dimensions?.width,
    height: dimensions?.height,
  } as React.CSSProperties;

  const content = React.useMemo(() => {
    if (!activeId) return null;
    if (typeof children === 'function') {
      return children({
        value: activeId,
        variant: isColumn(activeId) ? 'column' : 'item',
      });
    }
    return children;
  }, [activeId, children, isColumn]);

  return (
    <DragOverlay dropAnimation={dropAnimationConfig}>
      <div
        data-slot="kanban-overlay"
        data-dragging={true}
        style={style}
        className={cn(
          'pointer-events-none select-none',
          '[&_*]:!outline-none [&_*]:!shadow-none',
          className,
          activeId ? '!cursor-grabbing' : ''
        )}
      >
        {content}
      </div>
    </DragOverlay>
  );
}

export {
  Kanban,
  KanbanBoard,
  KanbanColumn,
  KanbanColumnHandle,
  KanbanItem,
  KanbanItemHandle,
  KanbanColumnContent,
  KanbanDropZone,
  KanbanOverlay,
};
